import { type UseFormSetError } from 'react-hook-form';
import { toast } from 'react-toastify/unstyled';

import type { Role } from '@app/utils/protected-routes.utils';
import { useModal } from '@contexts/modal-context/ModalContext';
import { useCreateUserWithResetPwLinkMutation } from '@features/user-management/api/user-management.api';
import type { CreateUserFormValues } from '@features/user-management/components/modals/create-new-user.schema';
import type { RTKQueryError } from '@shared/utils/api.types';

export const useCreateNewUser = () => {
  const [createNewUser, { isLoading, reset }] = useCreateUserWithResetPwLinkMutation();
  const { closeModal } = useModal();

  const handleOnClose = () => {
    closeModal('ADD_NEW_USER');
    reset?.();
  };

  const submitNewUser = async (data: CreateUserFormValues, setError: UseFormSetError<CreateUserFormValues>) => {
    try {
      await createNewUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        roles: [data.roleId as Role],
      }).unwrap();

      toast.success('User created successfully. A password reset link has been sent to their email.');

      handleOnClose();
    } catch (err) {
      console.error('Create new user failed:', err);
      const errorObj = (err as RTKQueryError)?.data as {
        message?: string;
        errors?: Record<string, string>;
      };
      const backendErrors = errorObj?.errors ?? {};
      // If there are field errors, set them
      Object.entries(backendErrors)
        .filter(([, value]) => typeof value === 'string' && value.length > 0)
        .forEach(([name, value]) => {
          setError(name as keyof CreateUserFormValues, { type: 'server', message: value });
        });
      // If there is a general message containing info about email, set it under the email field
      // (because it doesn't have the same structure as other field errors to set in the loop above)
      if (errorObj?.message?.includes('email')) {
        setError('email', { type: 'server', message: errorObj.message });
      }

      toast.error(errorObj?.message ?? 'Error creating new user');

      throw err;
    }
  };

  return {
    submitNewUser,
    isLoading,
    onCancel: handleOnClose,
  };
};
